import React from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Clock3 } from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { get_all_recordings } from "../../api";
import type { Recording } from "../../api/types";
import { formatDuration, parseUtcDate } from "../../utils";
import { useKeyedRequest } from "../../utils/useKeyedRequest";
import { buildSessionTitle } from "./sessionTitle";

/** Kept at page level so moving between sessions does not reload the person's directory. */
export function useSessionNeighbours(userId: string | undefined, recordingId: string | undefined) {
  const directory = useKeyedRequest(userId ?? null, async () => {
    const { recordings } = await get_all_recordings({ user_ids: [userId!] });
    return recordings
      .filter(item => item.user_id === userId)
      .sort(
        (a, b) =>
          parseUtcDate(a.date_started).getTime() - parseUtcDate(b.date_started).getTime() ||
          a.id.localeCompare(b.id),
      );
  });
  const items = directory.data ?? [];
  const index = items.findIndex(item => item.id === recordingId);
  return index < 0 ? null : { older: items[index - 1], newer: items[index + 1] };
}

export default function SessionNavigation({
  neighbours,
}: {
  neighbours: ReturnType<typeof useSessionNeighbours>;
}) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  if (!neighbours || (!neighbours.older && !neighbours.newer)) return null;
  // Run comparisons and process drill-downs belong to the old session. Keep list context.
  const params = new URLSearchParams(location.search);
  for (const key of ["run", "compare", "process", "instance"]) params.delete(key);
  const state = (location.state as Record<string, unknown> | null) ?? {};
  const control =
    "inline-flex h-8 items-center justify-center gap-1 rounded-md px-2 text-xs font-medium text-text-mid hover:bg-muted-surface hover:text-text focus-visible:outline-info aria-disabled:cursor-default aria-disabled:opacity-40 aria-disabled:hover:bg-transparent";
  return (
    <nav
      aria-label={t("activity.session_navigation")}
      className="inline-flex shrink-0 items-center rounded-lg border border-outline bg-surface p-0.5"
    >
      <Tooltip.Provider delayDuration={150}>
        {(["older", "newer"] as const).map(direction => {
          const item = neighbours[direction];
          const label = t(
            direction === "older" ? "activity.older_session" : "activity.newer_session",
          );
          const content = (
            <>
              {direction === "older" && <ChevronLeft size={14} aria-hidden="true" />}
              {label}
              {direction === "newer" && <ChevronRight size={14} aria-hidden="true" />}
            </>
          );
          const started = item ? parseUtcDate(item.date_started) : null;
          const duration =
            item?.date_ended && started
              ? Math.max(0, (parseUtcDate(item.date_ended).getTime() - started.getTime()) / 1000)
              : null;
          return (
            <Tooltip.Root key={`${direction}-${item?.id ?? "none"}`}>
              <Tooltip.Trigger asChild>
                {item ? (
                  <Link
                    aria-label={label}
                    className={control}
                    to={{
                      pathname: `/recordings/${encodeURIComponent(item.id)}/`,
                      search: params.toString(),
                    }}
                    state={{ ...state, userId: item.user_id }}
                  >
                    {content}
                  </Link>
                ) : (
                  <button type="button" aria-disabled="true" aria-label={label} className={control}>
                    {content}
                  </button>
                )}
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  side="bottom"
                  align="end"
                  sideOffset={8}
                  collisionPadding={12}
                  className="z-[120] max-w-[min(18rem,calc(100vw-24px))] rounded-lg border border-outline bg-surface p-3 text-left text-xs text-text shadow-lg"
                >
                  {item && started ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-4 text-text-dim">
                        <span>{label}</span>
                        {!item.include_in_processes && <span>{t("recording_archived")}</span>}
                      </div>
                      <p className="break-words text-sm font-semibold">
                        {buildSessionTitle(undefined, item.id, item)}
                      </p>
                      <div className="flex items-start gap-2 text-text-dim">
                        <CalendarDays size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
                        <span>
                          {started.toLocaleDateString(i18n.resolvedLanguage, {
                            dateStyle: "medium",
                          })}
                          {" · "}
                          {started.toLocaleTimeString(i18n.resolvedLanguage, {
                            timeStyle: "short",
                          })}
                        </span>
                      </div>
                      {duration !== null && (
                        <div className="flex items-center gap-2 text-text-dim">
                          <Clock3 size={14} aria-hidden="true" />
                          <span>{formatDuration(duration)}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    t(
                      direction === "older"
                        ? "activity.no_older_session"
                        : "activity.no_newer_session",
                    )
                  )}
                  <Tooltip.Arrow className="fill-surface" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          );
        })}
      </Tooltip.Provider>
    </nav>
  );
}
